import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface PValueBadgeProps {
  /** Two-sided p-value from t-test of insider's mean excess return vs SPY. */
  pValue: number | null | undefined;
  /** Sign of edge: positive = beats SPY, negative = trails SPY. */
  meanExcessReturn?: number | null;
  /** Sample size — if too small, render muted "n/a". */
  settledCount?: number | null;
  /** Compact variant for trade cards; default is table-cell sized. */
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Compact statistical-edge badge: `p=0.034 ★★` style.
 *   ★★★  p < 0.01   (strong)
 *   ★★   p < 0.05   (significant)
 *   ★    p < 0.10   (marginal)
 *   —    p ≥ 0.10 or insufficient n
 *
 * Color follows direction (green if mean excess > 0, red if < 0).
 * Returns a muted dash with tooltip when n < 3 settled trades.
 */
const PValueBadge = ({
  pValue,
  meanExcessReturn,
  settledCount,
  size = 'md',
  className = '',
}: PValueBadgeProps) => {
  const n = settledCount ?? null;
  const insufficient = n != null && n < 3;
  const hasValue = pValue != null && Number.isFinite(pValue) && !insufficient;

  const sign = meanExcessReturn == null ? 1 : meanExcessReturn >= 0 ? 1 : -1;

  let label = '—';
  let stars = '';
  let colorClass = 'text-muted-foreground';
  let tooltip = 'Not enough settled trades yet to compute statistical significance.';

  if (hasValue) {
    const p = pValue as number;
    label = p < 0.0001 ? 'p<.0001' : p < 0.01 ? `p=${p.toFixed(3)}` : `p=${p.toFixed(2)}`;

    if (p < 0.01) stars = '★★★';
    else if (p < 0.05) stars = '★★';
    else if (p < 0.10) stars = '★';

    if (p < 0.10) {
      colorClass = sign >= 0 ? 'text-positive' : 'text-negative';
    } else {
      colorClass = 'text-muted-foreground';
    }

    const direction = sign >= 0 ? 'outperforms' : 'underperforms';
    tooltip =
      p < 0.01
        ? `Strong evidence this insider ${direction} SPY (p < 0.01). Highly unlikely to be random.`
        : p < 0.05
        ? `Significant evidence this insider ${direction} SPY (p < 0.05).`
        : p < 0.10
        ? `Marginal evidence (p < 0.10). Borderline — could still be noise.`
        : `No statistical edge detected (p ≥ 0.10). Returns indistinguishable from random.`;
  } else if (insufficient) {
    label = '—';
    tooltip = `Only ${n} settled trade${n === 1 ? '' : 's'} — need at least 3 for a meaningful p-value.`;
  }

  const text = size === 'sm' ? 'text-[11px]' : 'text-xs';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex items-center gap-0.5 font-mono tabular-nums ${text} ${colorClass} ${className}`}
        >
          <span className="font-medium">{label}</span>
          {stars && <span aria-hidden="true">{stars}</span>}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
};

export default PValueBadge;
